class ComponentAccordion extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick.bind(this))
    this.refreshAccordion = this.refreshAccordion.bind(this)

    if (this.classList.contains('is-active')) {
      this.expandAccordion(this)
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick)
  }

  refreshAccordion(accordion) {
    const content = accordion.querySelector('.js-accordion-content')
    this.adjustHeight(content, accordion.classList.contains('is-active'))
  }

  handleClick(e) {
    const toggle = e.target.closest('.js-accordion-toggle')
    if (!toggle) return

    const accordion = toggle.closest('.c-accordion')
    const closeSiblings = accordion.classList.contains('is-close-siblings')
    this.toggleAccordion(accordion, closeSiblings)
  }

  toggleAccordion(accordion, closeSiblings) {
    if (!accordion) return

    const isActive = accordion.classList.contains('is-active')

    if (closeSiblings) {
      getSiblings(accordion).forEach((item) => {
        if (item.classList.contains('is-active')) this.collapseAccordion(item)
      })
    }

    isActive ? this.collapseAccordion(accordion) : this.expandAccordion(accordion)
  }

  expandAccordion(accordion) {
    const content = accordion.querySelector('.js-accordion-content')
    if (!content) return

    accordion.classList.add('is-active')
    accordion.setAttribute('aria-expanded', true)
    content.setAttribute('aria-hidden', false)

    this.adjustHeight(content, true)
  }

  collapseAccordion(accordion) {
    const content = accordion.querySelector('.js-accordion-content')
    if (!content) return

    accordion.classList.remove('is-active')
    accordion.setAttribute('aria-expanded', false)
    content.setAttribute('aria-hidden', true)

    this.adjustHeight(content, false)
  }

  adjustHeight(wrapperEl, isOpening) {
    if (!wrapperEl) return

    const clear = () => {
      wrapperEl.style.height = isOpening ? 'auto' : ''
      wrapperEl.removeEventListener('transitionend', clear)
    }

    if (isOpening) {
      wrapperEl.style.height = '0'
      wrapperEl.offsetHeight
      wrapperEl.style.height = wrapperEl.scrollHeight + 'px'
    } else {
      wrapperEl.style.height = wrapperEl.scrollHeight + 'px'
      wrapperEl.offsetHeight
      wrapperEl.style.height = '0'
    }

    wrapperEl.addEventListener('transitionend', clear)
  }
}
customElements.define('c-accordion', ComponentAccordion)

class CursorFollowLabel {
  constructor() {
    this.element = document.querySelector('c-cursor-label')
    this.label = document.querySelector('.c-cursor-label__text')

    if (!this.element || !this.label || vs.isTouchDevice) {
      return
    }
    this.currentSection = null
    this.isVisible = false
    this.animationTimeoutIds = []
    this.init()
  }

  init() {
    this.sections = document.querySelectorAll('[data-cursor-text]')
    this.sections.forEach((section) => {
      section.addEventListener('mouseenter', (e) => this.handleMouseEnter(e))
      section.addEventListener('mouseleave', (e) => this.handleMouseLeave(e))
      section.addEventListener('mousemove', (e) => this.handleMouseMove(e))
    })

    // Global mouse move for positioning
    document.addEventListener('mousemove', (e) => {
      if (this.isVisible) {
        this.updatePosition(e)
      }
    })
  }

  handleMouseEnter(e) {
    this.updatePosition(e)
    const section = e.currentTarget
    const delay = section.dataset.showingSpeed
    const text = section.dataset.cursorText
    if (text) {
      this.currentSection = section
      this.showLabel(text, delay)
    }
  }

  handleMouseLeave(e) {
    this.currentSection = null
    this.hideLabel()
  }

  handleMouseMove(e) {
    if (this.isVisible) {
      this.updatePosition(e)
    }
  }

  showLabel(text, delaySpeed) {
    // Clear any existing animation timeouts
    this.animationTimeoutIds.forEach((id) => clearTimeout(id))
    this.animationTimeoutIds = []

    this.label.textContent = ''
    this.element.classList.add('is-visible')
    this.isVisible = true

    const characters = text.split('')
    const delay = delaySpeed || 36

    characters.forEach((char, index) => {
      const timeoutId = setTimeout(() => {
        this.label.textContent += char
      }, delay * index)

      this.animationTimeoutIds.push(timeoutId)
    })
  }

  hideLabel() {
    // Clear any ongoing animation
    this.animationTimeoutIds.forEach((id) => clearTimeout(id))
    this.animationTimeoutIds = []

    this.element.classList.remove('is-visible')
    this.isVisible = false
  }

  updatePosition(e) {
    const x = e.clientX
    const y = e.clientY
    this.element.style.transform = `translate3d(${x}px, ${y}px, 0)`
  }
}
class ComponentCarousel extends HTMLElement {
  constructor() {
    super()
    this.emblaNode = this.querySelector('.js-embla')
    this.viewportNode = this.emblaNode.querySelector('.js-embla__viewport')
    this.slideCardNodes = this.querySelectorAll('.c-carousel__slide')
    this.prevButtonNode = this.querySelector('.js-embla-button-prev')
    this.nextButtonNode = this.querySelector('.js-embla-button-next')
    this.dotsNode = this.emblaNode.querySelector('.js-embla__dots')
    this.thumbNode = this.querySelector('.js-embla-thumbs')

    this.currentProgress = 0
    this.targetProgress = 0
    this.animationFrame = null
    this.isAnimating = false

    this.emblaOptions = JSON.parse(this.dataset.emblaOptions.replace(/'/g, '"').trim())
    this.emblaApi = null

    //Plugin
    this.enableWheel = this.dataset.emblaWheel === 'true'
    this.enableFade = this.dataset.emblaFade === 'true'
    this.enableAutoHeight = this.dataset.emblaAutoHeight === 'true'
    this.enableAutoplay = this.dataset.emblaAutoplay === 'true'
    this.enableClickToSlide = this.dataset.emblaClickToSlide === 'true'

    this.autoplayDuration = parseInt(this.dataset.emblaAutoplayInterval * 1000) || 3000
    this.enableAutoScroll = this.dataset.emblaAutoScroll === 'true'
  }

  connectedCallback() {
    this.init()
  }

  init() {
    if (!window.EmblaCarousel || !this.emblaNode) return

    const autoplay = EmblaCarouselAutoplay({
      playOnInit: this.enableAutoplay,
      delay: this.autoplayDuration,
      stopOnInteraction: false,
      stopOnMouseEnter: false,
    })

    const autoScrollSpeed = this.hasAttribute('data-embla-scroll-speed') ? parseInt(this.dataset.emblaScrollSpeed) : 2
    const autoScrollDelay = this.hasAttribute('data-embla-scroll-delay') ? parseInt(this.dataset.emblaScrollDelay) : 0
    const autoScrollStopOnMouseEnter = this.dataset.emblaScrollStopOnMouseEnter

    const autoScrollDirection = this.dataset.emblaScrollBackward === 'true' ? 'backward' : 'forward'

    const autoScroll = EmblaCarouselAutoScroll({
      playOnInit: this.enableAutoScroll,
      speed: autoScrollSpeed,
      startDelay: autoScrollDelay,
      direction: autoScrollDirection,
      stopOnInteraction: false,
      stopOnMouseEnter: autoScrollStopOnMouseEnter,
    })

    const plugins = [
      EmblaCarouselClassNames(),
      ...(this.enableWheel ? [EmblaCarouselWheelGestures()] : []),
      ...(this.enableAutoplay ? [autoplay] : []),
      ...(this.enableFade ? [EmblaCarouselFade()] : []),
      ...(this.enableAutoHeight ? [EmblaCarouselAutoHeight()] : []),
      ...(this.enableAutoScroll ? [autoScroll] : []),
    ]

    let options = this.emblaOptions
    if (typeof options.align === 'number') {
      options = {
        ...options,
        align: () => {
          return options.align
        },
      }
    }

    this.emblaApi = window.EmblaCarousel(this.viewportNode, options, plugins)

    if (this.prevButtonNode && this.nextButtonNode) {
      const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(
        this.emblaApi,
        this.prevButtonNode,
        this.nextButtonNode
      )
      this.emblaApi.on('destroy', removePrevNextBtnsClickHandlers)
    }

    if (this.dotsNode) {
      const removeDotBtnsAndClickHandlers = this.addDotBtnsAndClickHandlers(this.emblaApi, this.dotsNode)

      this.emblaApi.on('destroy', removeDotBtnsAndClickHandlers)
    }

    if (this.enableClickToSlide) {
      const removeClickToSlideHandler = this.addClickToSlideHandler(this.emblaApi, this.slideCardNodes)
      this.emblaApi.on('destroy', removeClickToSlideHandler)
    }

    if (this.enableAutoplay) {
      this.emblaApi.on('autoplay:play', this.startAutoplay.bind(this))
      this.emblaApi.on('autoplay:stop', this.stopAutoplay.bind(this))
      this.emblaApi.on('select', this.autoplaySelect.bind(this))
      this.emblaApi.on('init', this.startAutoplay.bind(this))

      if (this.dotsNode) {
        if (this.enableFade) {
          this.emblaApi.on('select', this.onSlideChangeProgress.bind(this))
        } else {
          this.emblaApi.on('scroll', this.onScrollAnimateDots.bind(this))
          this.emblaApi.on('settle', this.onScrollSettle.bind(this))
        }
      }
    }

    if (this.thumbNode) {
      this.initializeThumbnailsNavigation()
    }
    this.animateProgress()
  }
  // Thumbnail navigation
  initializeThumbnailsNavigation() {
    this.viewportThumbNode = this.thumbNode.querySelector('.js-embla-thumbs-viewport')
    this.prevThumbButtonNode = this.thumbNode.querySelector('.js-embla-thumbs-button-prev')
    this.nextThumbButtonNode = this.thumbNode.querySelector('.js-embla-thumbs-button-next')

    const options = {
      align: 'start',
      loop: false,
      breakpoints: {
        '(min-width: 1024px)': { axis: 'y', align: 'start', loop: true },
      },
    }

    this.emblaThumb = EmblaCarousel(this.viewportThumbNode, options, [EmblaCarouselWheelGestures()])

    // Add thumbnail prev/next button handlers
    if (this.prevThumbButtonNode && this.nextThumbButtonNode) {
      const removeThumbPrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(
        this.emblaThumb,
        this.prevThumbButtonNode,
        this.nextThumbButtonNode
      )
      this.emblaThumb.on('destroy', removeThumbPrevNextBtnsClickHandlers)
    }

    this.addThumbBtnsClickHandlers(this.emblaApi, this.emblaThumb)
    this.toggleThumbBtnsActive(this.emblaApi, this.emblaThumb)

    const handleThumbResize = () => {
      const isExceedViewport = this.emblaThumb.scrollSnapList().length > 1
      this.viewportThumbNode.classList.toggle('is-justify-center', !isExceedViewport)
      this.emblaThumb.reInit({ watchDrag: isExceedViewport })
    }

    // justify center thumbnails when container does not exceed viewport
    handleThumbResize()
    this.emblaApi.on('resize', handleThumbResize)

    // prevent overscrolling
    this.emblaThumb.on('scroll', (emblaApi) => {
      const { limit, target, location, offsetLocation, scrollTo, translate, scrollBody } = emblaApi.internalEngine()

      let edge

      if (limit.reachedMax(target.get())) edge = limit.max
      if (limit.reachedMin(target.get())) edge = limit.min

      if (!options.loop && edge !== undefined) {
        offsetLocation.set(edge)
        location.set(edge)
        target.set(edge)
        translate.to(edge)
        translate.toggleActive(false)
        scrollBody.useDuration(0).useFriction(0)
        scrollTo.distance(0, false)
      } else {
        translate.toggleActive(true)
      }
    })
  }

  addThumbBtnsClickHandlers(emblaApi, emblaThumb) {
    const slidesThumbs = emblaThumb.slideNodes()
    const scrollToIndex = slidesThumbs.map((_, index) => () => emblaApi.scrollTo(index))
    slidesThumbs.forEach((slideNode, index) => {
      slideNode.addEventListener('click', scrollToIndex[index], false)
    })

    this.removeThumbBtnsClickHandlers = () => {
      slidesThumbs.forEach((slideNode, index) => {
        slideNode.removeEventListener('click', scrollToIndex[index], false)
      })
    }
  }

  toggleThumbBtnsActive(emblaApi, emblaThumb) {
    const slidesThumbs = emblaThumb.slideNodes()
    const toggleThumbBtnsState = () => {
      emblaThumb.scrollTo(emblaApi.selectedScrollSnap())
      const previous = emblaApi.previousScrollSnap()
      const selected = emblaApi.selectedScrollSnap()
      slidesThumbs[previous].classList.remove('is-selected')
      slidesThumbs[selected].classList.add('is-selected')
    }

    emblaApi.on('select', toggleThumbBtnsState)
    emblaThumb.on('init', toggleThumbBtnsState)

    this.removeToggleThumbBtnsActive = () => {
      const selected = emblaApi.selectedScrollSnap()
      slidesThumbs[selected].classList.remove('is-selected')
    }
  }

  // Prev & Next Buttons
  addTogglePrevNextBtnsActive = (emblaApi, prevBtn, nextBtn) => {
    const togglePrevNextBtnsState = () => {
      if (emblaApi.canScrollPrev()) prevBtn.removeAttribute('disabled')
      else prevBtn.setAttribute('disabled', 'disabled')

      if (emblaApi.canScrollNext()) nextBtn.removeAttribute('disabled')
      else nextBtn.setAttribute('disabled', 'disabled')
    }

    emblaApi
      .on('select', togglePrevNextBtnsState)
      .on('init', togglePrevNextBtnsState)
      .on('reInit', togglePrevNextBtnsState)

    return () => {
      prevBtn.removeAttribute('disabled')
      nextBtn.removeAttribute('disabled')
    }
  }

  addPrevNextBtnsClickHandlers = (emblaApi, prevBtn, nextBtn) => {
    const scrollPrev = () => {
      emblaApi.scrollPrev()
    }

    const scrollNext = () => {
      emblaApi.scrollNext()
    }
    prevBtn.addEventListener('click', scrollPrev, false)
    nextBtn.addEventListener('click', scrollNext, false)

    const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(emblaApi, prevBtn, nextBtn)

    return () => {
      removeTogglePrevNextBtnsActive()
      prevBtn.removeEventListener('click', scrollPrev, false)
      nextBtn.removeEventListener('click', scrollNext, false)
    }
  }

  addDotBtnsAndClickHandlers = (emblaApi, dotsNode) => {
    let dotNodes = []

    const addDotBtnsWithClickHandlers = () => {
      dotsNode.innerHTML = emblaApi
        .scrollSnapList()
        .map((_, index) => {
          const dataTitle = this.slideCardNodes[index].dataset.title || `go to number ${index} slide`

          return `<li role="presentation" class="c-carousel__dot" style="--item-index: ${index};"><button type="button" role="tab" class="c-carousel__dot__button increase-target-size"><span class="sr-only">${dataTitle}</span></button></li>`
        })
        .join('')

      const scrollTo = (index) => {
        emblaApi.scrollTo(index)
      }

      dotNodes = Array.from(dotsNode.querySelectorAll('.c-carousel__dot'))

      dotNodes.forEach((dotNode, index) => {
        const button = dotNode.querySelector('.c-carousel__dot__button')
        button.addEventListener('click', () => scrollTo(index), false)
      })
    }

    const toggleDotBtnsActive = () => {
      const previous = emblaApi.previousScrollSnap()
      const selected = emblaApi.selectedScrollSnap()
      dotNodes[previous].classList.remove('c-carousel__dot--selected')
      dotNodes[selected].classList.add('c-carousel__dot--selected')
    }

    emblaApi
      .on('init', addDotBtnsWithClickHandlers)
      .on('reInit', addDotBtnsWithClickHandlers)
      .on('init', toggleDotBtnsActive)
      .on('reInit', toggleDotBtnsActive)
      .on('select', toggleDotBtnsActive)

    return () => {
      dotsNode.innerHTML = ''
    }
  }

  addClickToSlideHandler = (emblaApi, slideCards) => {
    const scrollTo = (index) => emblaApi.scrollTo(index)

    slideCards.forEach((card, index) => card.addEventListener('click', () => scrollTo(index), false))

    return () => {
      slideCards.forEach((card, index) => card.removeEventListener('click', () => scrollTo(index), false))
    }
  }

  autoplaySelect = (e) => {
    const autoplay = e?.plugins()?.autoplay
    const resetOrStop = autoplay.options.stopOnInteraction === false ? autoplay.reset : autoplay.stop

    resetOrStop()
  }

  startAutoplay() {
    this.classList.remove('is-autoplay-paused')
    this.classList.add('is-autoplay-playing')
  }

  stopAutoplay() {
    this.classList.remove('is-autoplay-playing')
    this.classList.add('is-autoplay-paused')
  }

  onSlideChangeProgress(e) {
    const slideCount = e.slideNodes().length
    if (slideCount === 1) return

    const selectedIndex = e.selectedScrollSnap()
    const previousIndex = e.previousScrollSnap()

    let newTarget = selectedIndex

    // Handle loop: last slide to first
    if (previousIndex === slideCount - 1 && selectedIndex === 0) {
      // Going from last to first (forward loop)
      newTarget = 0
    }
    // Handle loop: first slide to last
    else if (previousIndex === 0 && selectedIndex === slideCount - 1) {
      // Going from first to last
      newTarget = slideCount - 1
    }

    this.targetProgress = newTarget

    if (!this.isAnimating) {
      this.isAnimating = true
      this.animateProgress()
    }
  }
  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3)
  }

  animateProgress() {
    const ease = 0.25 // Adjust for smoother/faster transition (0.05 = slower, 0.2 = faster)
    const threshold = 0.0001 // When to snap to target

    // Ease towards target
    const diff = this.targetProgress - this.currentProgress
    this.currentProgress += diff * ease

    if (Math.abs(diff) < threshold) {
      this.currentProgress = this.targetProgress

      const slideCount = this.emblaApi?.slideNodes().length || 0
      if (this.currentProgress >= slideCount) {
        this.currentProgress = 0
        this.targetProgress = 0
      }

      this.isAnimating = false
    }

    const displayProgress = this.easeOutCubic(this.currentProgress)

    this.style.setProperty('--autoplay-progress', displayProgress.toFixed(6))

    if (this.isAnimating) {
      this.animationFrame = requestAnimationFrame(() => this.animateProgress())
    }
  }

  onScrollAnimateDots(e) {
    const slideCount = e.slideNodes().length
    if (slideCount === 1) return

    const scrollProgress = e.scrollProgress()
    const totalProgress = scrollProgress * (slideCount - 1)

    const normalizeProgress = ((scrollProgress % 1) + 1) % 1
    const progress = normalizeProgress * slideCount
    let autoplayProgress = progress

    if (slideCount === 2) {
      autoplayProgress = Math.max(0, Math.min(1, totalProgress))
    } else {
      if (progress < 0) {
        autoplayProgress = 0
      } else if (progress > slideCount - 1) {
        const excess = progress - (slideCount - 1)
        autoplayProgress = (slideCount - 1) * (1 - excess) // reverse direction
      }
    }

    this.style.setProperty('--autoplay-progress', autoplayProgress.toFixed(6))
  }

  onScrollSettle(e) {
    const selectedIndex = e.selectedScrollSnap()
    this.style.setProperty('--autoplay-progress', selectedIndex)
  }

  disconnectedCallback() {
    if (this.emblaApi) {
      this.emblaApi.destroy()
    }
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
    }
    this.isAnimating = false
  }
}
customElements.define('c-carousel', ComponentCarousel)
class TabsComponent extends HTMLElement {
  constructor() {
    super()
    this.activeTabIndex = 0
    this.tabs = []
    this.panels = []
    this.isAnimating = false
  }

  connectedCallback() {
    this.init()
    this.setupEventListeners()
    this.setupKeyboardNavigation()
    this.setupSwipeGestures()
  }

  disconnectedCallback() {
    this.cleanup()
  }

  init() {
    // Cache DOM elements
    this.tabsNav = this.querySelector('.c-tabs__nav')
    this.tabsContent = this.querySelector('.c-tabs__content')
    this.tabs = Array.from(this.querySelectorAll('.c-tabs__trigger'))
    this.tabActions = Array.from(this.querySelectorAll('.c-tabs__actions'))
    this.panels = Array.from(this.querySelectorAll('.c-tabs__panel'))

    // Set initial state
    this.setInitialActiveTab()
    this.setupAccessibility()
  }

  setInitialActiveTab() {
    const activeTab = this.tabs.find((tab) => tab.classList.contains('is-active'))
    this.activeTabIndex = activeTab ? this.tabs.indexOf(activeTab) : 0
    this.updateTabsState()
  }

  setupAccessibility() {
    // Ensure proper ARIA attributes
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('tabindex', index === this.activeTabIndex ? '0' : '-1')
    })
  }

  setupEventListeners() {
    // Tab click handlers
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault()
        this.switchToTab(index)
      })
    })
  }

  setupKeyboardNavigation() {
    this.tabsNav.addEventListener('keydown', (e) => {
      const { key } = e
      let newIndex = this.activeTabIndex

      switch (key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault()
          newIndex = this.activeTabIndex > 0 ? this.activeTabIndex - 1 : this.tabs.length - 1
          break
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault()
          newIndex = this.activeTabIndex < this.tabs.length - 1 ? this.activeTabIndex + 1 : 0
          break
        case 'Home':
          e.preventDefault()
          newIndex = 0
          break
        case 'End':
          e.preventDefault()
          newIndex = this.tabs.length - 1
          break
        case 'Enter':
        case ' ':
          e.preventDefault()
          // Tab is already active, no need to switch
          return
        default:
          return
      }

      this.switchToTab(newIndex)
      this.tabs[newIndex].focus()
    })
  }

  setupSwipeGestures() {
    let startX = 0
    let startY = 0
    let distX = 0
    let distY = 0
    const threshold = 50

    this.tabsContent.addEventListener(
      'touchstart',
      (e) => {
        const touch = e.touches[0]
        startX = touch.clientX
        startY = touch.clientY
      },
      { passive: true }
    )

    this.tabsContent.addEventListener(
      'touchmove',
      (e) => {
        if (!startX || !startY) return

        const touch = e.touches[0]
        distX = touch.clientX - startX
        distY = touch.clientY - startY
      },
      { passive: true }
    )

    this.tabsContent.addEventListener(
      'touchend',
      () => {
        if (Math.abs(distX) < threshold || Math.abs(distY) > Math.abs(distX)) {
          startX = startY = distX = distY = 0
          return
        }
        startX = startY = distX = distY = 0
      },
      { passive: true }
    )
  }

  switchToTab(newIndex) {
    if (newIndex === this.activeTabIndex || this.isAnimating || newIndex < 0 || newIndex >= this.tabs.length) {
      return
    }

    this.isAnimating = true

    // Trigger custom event
    this.dispatchEvent(
      new CustomEvent('tabSwitch', {
        detail: {
          previousIndex: this.activeTabIndex,
          newIndex: newIndex,
          previousTab: this.tabs[this.activeTabIndex],
          newTab: this.tabs[newIndex],
        },
      })
    )

    this.activeTabIndex = newIndex
    this.updateTabsState()

    // Reset animation flag after transition
    setTimeout(() => {
      this.isAnimating = false
    }, 300)
  }

  updateTabsState() {
    this.tabs.forEach((tab, index) => {
      const isActive = index === this.activeTabIndex
      tab.classList.toggle('is-active', isActive)
      tab.setAttribute('aria-selected', isActive.toString())
      tab.setAttribute('tabindex', isActive ? '0' : '-1')
    })

    // Update panels
    this.panels.forEach((panel, index) => {
      const isActive = index === this.activeTabIndex
      panel.classList.toggle('is-active', isActive)
      panel.setAttribute('aria-hidden', (!isActive).toString())
    })

    this.tabActions.forEach((item, index) => {
      const isActive = index === this.activeTabIndex

      item.classList.toggle('is-active', isActive)
      item.setAttribute('tabindex', isActive ? '0' : '-1')
    })
    // Update indicator if present
    this.updateTabIndicator()
  }

  updateTabIndicator() {
    const indicator = this.querySelector('.c-tabs__indicator')
    if (!indicator || !this.tabs[this.activeTabIndex]) return

    const activeTab = this.tabs[this.activeTabIndex]
    const { offsetLeft, offsetWidth } = activeTab

    indicator.style.transform = `translateX(${offsetLeft}px)`
    indicator.style.width = `${offsetWidth}px`
  }

  // Public methods
  goToTab(index) {
    this.switchToTab(index)
  }

  nextTab() {
    const nextIndex = this.activeTabIndex < this.tabs.length - 1 ? this.activeTabIndex + 1 : 0
    this.switchToTab(nextIndex)
  }

  prevTab() {
    const prevIndex = this.activeTabIndex > 0 ? this.activeTabIndex - 1 : this.tabs.length - 1
    this.switchToTab(prevIndex)
  }

  getCurrentTab() {
    return {
      index: this.activeTabIndex,
      tab: this.tabs[this.activeTabIndex],
      panel: this.panels[this.activeTabIndex],
    }
  }

  // Shopify theme editor support
  handleSectionLoad(event) {
    if (this.closest(`[data-section-id="${event.detail.sectionId}"]`)) {
      this.init()
    }
  }

  handleSectionReorder() {
    this.init()
  }

  // Cleanup
  cleanup() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  // Handle responsive behavior
  handleResize() {
    this.updateTabIndicator()
  }
}
customElements.define('c-tabs', TabsComponent)

class ComponentFeaturesMarquee extends HTMLElement {
  constructor() {
    super()
    this.carouselElement = null
    this.resizeObserver = null
    this.checkTimeout = null
    this.originalItems = []
    this.slideSize = 0
  }

  connectedCallback() {
    this.slideSize = this.querySelector('.c-carousel__slide').clientWidth
    this.carouselElement = this.querySelector('c-carousel')
    if (!this.carouselElement) {
      console.warn('ComponentFeaturesMarquee: No c-carousel element found')
      return
    }

    // Store original items before any duplication
    const container = this.carouselElement.querySelector('.c-carousel__container')
    if (container) {
      this.originalItems = Array.from(container.children)
    }
    this.waitForCarouselReady()
    this.setupResizeObserver()
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    if (this.checkTimeout) {
      clearTimeout(this.checkTimeout)
    }
  }

  waitForCarouselReady() {
    if (this.carouselElement.emblaApi) {
      this.checkAndInitialize()
    } else {
      this.checkTimeout = setTimeout(() => this.waitForCarouselReady(), 100)
    }
  }

  checkAndInitialize() {
    const shouldActivate = this.shouldActivateCarousel()

    if (shouldActivate) {
      this.activateCarousel()
    } else {
      this.deactivateCarousel()
    }
  }

  shouldActivateCarousel() {
    const itemCount = this.originalItems.length
    if (itemCount === 0) return false

    const itemWidth = this.slideSize
    const totalItemsWidth = itemCount * itemWidth
    const availableWidth = window.innerWidth
    return totalItemsWidth > availableWidth
  }

  duplicateItemsForMarquee() {
    const container = this.carouselElement.querySelector('.c-carousel__container')
    if (!container) return

    const itemWidth = this.slideSize
    const viewportWidth = window.innerWidth

    // Calculate how many times we need to duplicate to fill at least 3x viewport width
    // This ensures smooth infinite scrolling
    const singleSetWidth = this.originalItems.length * itemWidth
    const targetWidth = viewportWidth * 3
    const duplicationsNeeded = Math.ceil(targetWidth / singleSetWidth)

    // Clear container and add duplicated items
    container.innerHTML = ''

    // Add original items plus duplicates
    for (let i = 0; i < duplicationsNeeded; i++) {
      this.originalItems.forEach((item) => {
        const clone = item.cloneNode(true)
        clone.setAttribute('data-cloned', 'true')
        container.appendChild(clone)
      })
    }
  }

  restoreOriginalItems() {
    const container = this.carouselElement.querySelector('.c-carousel__container')
    if (!container) return

    container.innerHTML = ''
    this.originalItems.forEach((item) => {
      item.removeAttribute('data-cloned')
      container.appendChild(item)
    })
  }

  activateCarousel() {
    if (!this.carouselElement) return

    this.duplicateItemsForMarquee()
    if (this.carouselElement.emblaApi) {
      const emblaApi = this.carouselElement.emblaApi
      emblaApi.reInit({
        loop: true,
        watchDrag: true,
        align: 'start',
        containScroll: false,
        dragFree: false,
      })

      const container = this.carouselElement.querySelector('.c-carousel__container')
      if (container) {
        container.style.justifyContent = ''
      }
    }

    this.classList.add('marquee-active')

    if (this.carouselElement.startAutoscroll) {
      this.carouselElement.startAutoscroll()
    }
  }

  deactivateCarousel() {
    if (!this.carouselElement) return

    if (this.carouselElement.stopAutoscroll) {
      this.carouselElement.stopAutoscroll()
    }
    this.restoreOriginalItems()

    if (this.carouselElement.emblaApi) {
      const emblaApi = this.carouselElement.emblaApi

      emblaApi.reInit({
        loop: false,
        watchDrag: false,
        align: 'center',
        containScroll: 'trimSnaps',
      })
    }

    this.classList.remove('marquee-active')
  }

  setupResizeObserver() {
    let resizeTimeout

    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        this.checkAndInitialize()
      }, 150)
    })

    this.resizeObserver.observe(document.body)
  }
}
customElements.define('c-features-marquee', ComponentFeaturesMarquee)

// place global, component, and page level functions here
const gHeader = {
  // DOM elements cache
  elements: {
    root: null,
    mobileMenu: null,
    mobileMenuInner: null,
    header: null,
    search: null,
    megamenus: null,
  },
  scrollTimeout: null,
  THROTTLE_DURATION: 100,
  // Initialize DOM elements
  initElements: function () {
    this.elements.root = document.documentElement
    this.elements.mobileMenu = document.querySelector('.js-mobile-menu')
    this.elements.mobileMenuInner = document.querySelector('.js-mobile-menu-inner')
    this.elements.header = document.querySelector('.g-header')
    this.elements.search = document.querySelector('.js-g-search')
    this.elements.megamenus = document.querySelectorAll('[data-megamenu]')
  },

  // *** Megamenu ***
  megamenuOpen: function (trigger) {
    const targetName = trigger.dataset.megamenuToggle

    const targetMenus = this.elements.header?.querySelectorAll(`[data-megamenu="${targetName}"]`)

    if (!targetMenus && targetMenus.length == 0) return

    // Close other megamenus first
    this.megamenuCloseAll()
    root.classList.remove('is-search-active')

    // Open this megamenu
    this.elements.root.classList.add('is-megamenu-active')
    trigger.closest('li').classList.add('is-megamenu-active')

    targetMenus.forEach((menu) => {
      menu.classList.add('is-active')
      // focusTrapManager.activate(menu, trigger)
      setAriaHiddenAttributes(menu, true)
    })

    scrollHandler.scrollDisable()
  },

  megamenuClose: function (trigger) {
    const targetName = trigger.dataset.megamenuToggle
    const targetMenu = this.elements.header?.querySelector(`[data-megamenu="${targetName}"]`)

    if (!targetMenu) return

    this.elements.root.classList.remove('is-megamenu-active')
    this.megamenuCloseAll()
    scrollHandler.scrollEnable()
    // focusTrapManager.deactivate(targetMenu)
  },

  megamenuCloseAll: function (enableScroll = true) {
    this.elements.root.classList.remove('is-megamenu-active')

    document.querySelectorAll('[data-megamenu].is-active').forEach((menu) => {
      menu.classList.remove('is-active')
      setAriaHiddenAttributes(menu, false)
    })

    document.querySelectorAll('.is-megamenu-active').forEach((el) => {
      el.classList.remove('is-megamenu-active')
    })

    if (enableScroll) {
      scrollHandler.scrollEnable()
    }
  },

  megamenuToggle: function (trigger) {
    const isActive = trigger.classList.contains('is-megamenu-active')

    if (isActive) {
      this.megamenuClose(trigger)
    } else {
      this.megamenuOpen(trigger)
    }
  },

  initMegamenu: function () {
    // Tablet setup
    setTimeout(() => {
      if (vs.isTabletDown) return

      document.querySelectorAll('[data-megamenu-toggle]').forEach((el) => {
        el.dataset.pageTransition = 'none'
        el.removeAttribute('data-page-transition')
      })
    })

    // Hover event listeners
    let hoverTimeout = null

    // Mouse enter on trigger
    on('body', 'mouseover', '[data-megamenu-toggle]', (e) => {
      if (vs.isTabletDown) return

      clearTimeout(hoverTimeout)
      const trigger = e.target.closest('[data-megamenu-toggle]')
      this.megamenuOpen(trigger)
    })

    // Keyboard focus on trigger
    on('body', 'focusin', '[data-megamenu-toggle]', (e) => {
      if (vs.isTabletDown) return

      clearTimeout(hoverTimeout)
      const trigger = e.target.closest('[data-megamenu-toggle]')
      this.megamenuOpen(trigger)
    })

    // Arrow down enters megamenu
    document.addEventListener('keydown', (e) => {
      if (vs.isTabletDown) return
      if (e.key !== 'ArrowDown') return

      const trigger = e.target.closest('[data-megamenu-toggle]')
      if (!trigger) return

      e.preventDefault()

      this.megamenuOpen(trigger)

      const targetName = trigger.dataset.megamenuToggle
      const targetMenu = this.elements.header?.querySelector(`[data-megamenu="${targetName}"]`)

      const firstFocusable = targetMenu?.querySelector(
        'a[href], [tabindex]:not([tabindex="-1"])'
      )

      firstFocusable?.focus()
    })

    // Mouse enter on trigger
    on('body', 'click', '[data-megamenu-toggle]', (e) => {
      const trigger = e.target.closest('[data-megamenu-toggle]')
      this.megamenuOpen(trigger)
    })

    // Mouse leave on trigger
    on('body', 'mouseout', '[data-megamenu-toggle]', (e) => {
      if (vs.isTabletDown) return

      const trigger = e.target.closest('[data-megamenu-toggle]')
      const targetName = trigger.dataset.megamenuToggle
      const targetMenu = this.elements.header?.querySelector(`[data-megamenu="${targetName}"]`)

      // Delay closing to allow mouse to move to megamenu
      hoverTimeout = setTimeout(() => {
        if (targetMenu && !targetMenu.matches(':hover') && !trigger.matches(':hover')) {
          this.megamenuClose(trigger)
        }
      }, 100)
    })

    // Mouse enter on megamenu - cancel closing
    on('body', 'mouseenter', '[data-megamenu]', (e) => {
      if (vs.isTabletDown) return
      clearTimeout(hoverTimeout)
    })

    // Mouse leave on megamenu - close it
    on('body', 'mouseout', '[data-megamenu]', (e) => {
      if (vs.isTabletDown) return

      const menu = e.target.closest('[data-megamenu]')
      const menuName = menu.dataset.megamenu
      const trigger = document.querySelector(`[data-megamenu-toggle="${menuName}"]`)

      hoverTimeout = setTimeout(() => {
        if (trigger && !trigger.matches(':hover') && !menu.matches(':hover')) {
          this.megamenuClose(trigger)
        }
      }, 100)
    })

    // Keep existing close handlers
    on('body', 'click', '.js-megamenu-close, .js-g-overlay', () => {
      this.megamenuCloseAll()
    })

    on('body', 'click', '.js-megamenu-back', () => {
      this.megamenuCloseAll(false)
    })

    on('body', 'keydown', 'body', (e) => {
      if (e.key === 'Escape' && vs.isTabletUp) {
        this.megamenuCloseAll()
      }
    })
  },

  /*** Mobile Menu ***/
  mobileMenuOpen: function () {
    this.elements.root.classList.add('is-mobile-menu-active')

    if (this.elements.mobileMenu) {
      setAriaHiddenAttributes(this.elements.mobileMenu, true)
    }

    if (globalOverlay) {
      setAriaHiddenAttributes(globalOverlay, true)
    }

    scrollHandler.scrollDisable()
  },

  mobileMenuClose: function () {
    this.elements.root.classList.remove('is-mobile-menu-active')

    // close megamenu & search
    setTimeout(() => {
      this.megamenuCloseAll()

      this.elements.mobileMenu.querySelector('.js-g-search').classList.remove('is-mobile-search-active')
    }, 400)

    if (this.elements.mobileMenu) {
      setAriaHiddenAttributes(this.elements.mobileMenu, false)
    }

    if (globalOverlay) {
      setAriaHiddenAttributes(globalOverlay, false)
    }
    scrollHandler.scrollEnable()
  },

  mobileMenuToggle: function () {
    const isActive = this.elements.root.classList.contains('is-mobile-menu-active')

    if (isActive) {
      this.mobileMenuClose()
    } else {
      this.mobileMenuOpen()
    }
  },

  initMobileMenu: function () {
    on('body', 'click', '.js-mobile-menu-toggle', () => {
      this.mobileMenuToggle()
    })

    on('body', 'click', '.js-g-overlay', () => {
      if (this.elements.root.classList.contains('is-mobile-menu-active')) {
        this.mobileMenuClose()
      }
    })

    on('body', 'keydown', 'body', (e) => {
      if (e.key === 'Escape' && this.elements.root.classList.contains('is-mobile-menu-active')) {
        this.mobileMenuClose()
      }
    })

    // Clone all megamenus and append to mobile menu
    if (this.elements.megamenus?.length > 0 && this.elements.mobileMenu) {
      this.elements.megamenus.forEach((megamenu) => {
        const megamenuClone = megamenu.cloneNode(true)
        megamenuClone.removeAttribute('tabindex')
        megamenuClone.removeAttribute('aria-hidden')
        megamenuClone.classList.remove('tablet-up-only')
        this.elements.mobileMenu.appendChild(megamenuClone)
      })
    }
  },

  // *** Mobile Menu Search ***
  initSearch: function () {
    // Legacy g-header cloned the search panel into its mobile menu. That
    // markup no longer exists in the oddit header, so only clone when present.
    if (this.elements.search && this.elements.mobileMenuInner) {
      const searchClone = this.elements.search.cloneNode(true)
      searchClone.removeAttribute('tabindex')
      searchClone.removeAttribute('aria-hidden')

      //Prevent HTML error in customizer editor
      searchClone.removeAttribute('id')
      searchClone.removeAttribute('data-shopify-editor-section')

      searchClone.classList.remove('p-x-max', 'shopify-section')

      this.elements.mobileMenuInner.insertBefore(searchClone, this.elements.mobileMenuInner.firstChild)
    }

    // Wire up the search panel/toggle whenever the panel exists, independent
    // of the legacy mobile-menu clone path.
    if (this.elements.search) {
      gSearch.init()
    }
  },
  setupScrollListener: function () {
    this.handleHeaderScroll()
    window.addEventListener('scroll', this.handleHeaderScroll.bind(this))
  },
  handleHeaderScroll: function () {
    if (!this.elements.header || this.elements.header.offsetHeight === 0) {
      return
    }

    if (this.scrollTimeout) {
      return
    }

    this.scrollTimeout = setTimeout(() => {
      const { top: headerTopSpace, height: headerHeight } = this.elements.header.getBoundingClientRect()
      const headerSpace = headerTopSpace + headerHeight

      this.elements.root.style.setProperty('--s-header-space', `${headerSpace}px`)

      this.scrollTimeout = null
    }, this.THROTTLE_DURATION)
  },
  init: function () {
    this.initElements()
    this.initMegamenu()
    this.initMobileMenu()
    this.initSearch()
    this.setupScrollListener()
  },
}

// ***SEARCH***
const gSearch = {
  searchToggle: document.querySelector('.js-search-toggle'),
  searchGlobal: document.querySelector('.js-g-search'),
  searchContent: document.querySelector('.js-g-search .g-search__content'),
  searchInput: document.querySelector('.js-g-search .js-search-input'),
  searchForms: document.querySelectorAll('.js-search-form'),
  liveSearch: function (query, target) {
    loader.updateProgress(root)

    const searchParent = target.closest('.js-g-search')
    const searchResults = searchParent.querySelector('.js-search-results')
    const searchResultsStates = searchParent.querySelector('[data-search-states]')
    let searchUrl = `/search?q=${encodeURIComponent(query)}&type=product`

    fetch(searchUrl)
      .then(function (response) {
        return response.text()
      })
      .then((data) => {
        const parser = new DOMParser()
        const dataDoc = parser.parseFromString(data, 'text/html')
        const dataSearchResults = dataDoc.querySelector('.js-search-page-results')
        const dataSearchGrid = dataDoc.querySelector('.js-search-page-product')
        const dataSearchItems = dataSearchGrid ? dataSearchGrid.querySelectorAll('.js-search-item') : []

        dataDoc.querySelector('.search-content__suggestion')?.remove()

        if (dataSearchItems.length > 0 && query != '') {
          //Apply tabs styling & prevent changing tab title event
          dataSearchResults.querySelectorAll('.js-search-page-tabs > button').forEach((tab) => {
            tab.classList.add('bg-hover-black')
            tab.removeAttribute('data-search-url-apply')
          })

          dataSearchResults?.querySelector('[data-match-target="product"]').classList.add('is-active')

          searchResults.innerHTML = dataSearchResults.innerHTML

          cInfiniteScroll.init()

          const triggerSearchUrl = searchParent.querySelector('[data-search-url]').dataset.searchUrl

          this.triggerSearch(triggerSearchUrl, searchParent.querySelector('.js-search-parent'))

          searchResultsStates.dataset.searchStates = 'results'
        } else if (query == '') {
          searchResultsStates.dataset.searchStates = 'default'
        } else {
          searchResultsStates.dataset.searchStates = 'empty'
        }

        loader.complete(root)
      })
      .catch((error) => {
        console.error('Error:', error)
      })
  },
  triggerSearch: function (searchUrl, searchParentEl) {
    fetch(searchUrl)
      .then(function (response) {
        return response.text()
      })
      .then((data) => {
        const parser = new DOMParser()
        const dataDoc = parser.parseFromString(data, 'text/html')
        const dataSearchContainer = dataDoc.querySelector('.js-search-page-container')

        const container = searchParentEl
          ? searchParentEl.querySelector('.js-search-page-container-empty')
          : document.querySelector('.js-search-page-results .js-search-page-container-empty')

        if (dataSearchContainer) {
          container.innerHTML = dataSearchContainer.innerHTML

          // Re-init infinite scroll
          // cInfiniteScroll.init();
        }
      })
      .catch((error) => {
        console.error('Error:', error)
      })
  },
  searchClose: function (closedByKeyboard = false) {
    setAriaHiddenAttributes(this.searchContent, false)
    this.searchInput.blur()
    root.classList.remove('is-search-active')
    scrollHandler.scrollEnable()
    focusTrapManager.deactivate(this.searchGlobal, closedByKeyboard)
  },
  searchOpen: function () {
    scrollHandler.scrollDisable()
    setAriaHiddenAttributes(this.searchContent, true)
    root.classList.add('is-search-active')

    focusTrapManager.activate(this.searchGlobal, this.searchToggle)

    if (window.matchMedia('(min-width: 601px)').matches) {
      this.searchInput.focus()
    }
  },
  searchClear: function (form) {
    const formSearchResultStates = form?.closest('.js-g-search')?.querySelector('[data-search-states]')
    const input = form.querySelector('.js-search-input')
    input.value = ''
    form.classList.remove('search-has-value')

    if (formSearchResultStates) {
      formSearchResultStates.dataset.searchStates = 'default'
    }
    input.focus()
  },
  checkInitialArticleSearch: function () {
    const urlParams = new URLSearchParams(window.location.search)
    const query = urlParams.get('q')
    const type = urlParams.get('type')

    // Check if URL has q parameter and type is 'article' or 'product'
    if (query && (type === 'article' || type === 'product')) {
      // Toggle the type: article -> product, product -> article
      const newType = type === 'article' ? 'product' : 'article'

      // Build the search URL with the toggled type
      const searchUrl = `${window.location.pathname}?q=${encodeURIComponent(query)}&type=${newType}`

      const parentEl = document.querySelector('[data-search-url]')?.closest('.js-search-parent')

      // Activate the correct grid container
      parentEl.querySelector(`[data-match-target="${type}"]`).classList.add('is-active')

      this.triggerSearch(searchUrl, parentEl)
    }
  },
  initSearchPanel: function () {
    // Verify has value
    if (this.searchForms.length > 0) {
      this.searchForms.forEach((form) => {
        const input = form.querySelector('.js-search-input')
        input.value ? form.classList.add('search-has-value') : ''
      })
    }

    on('body', 'input', '.js-search-input', (e) => {
      if (e.code === 'Space') return
      const target = e.target.closest('.js-search-input')
      let query = e.target.value

      target.closest('.js-search-form').classList.toggle('search-has-value', !!query)

      debounce(() => {
        this.liveSearch(query, target)
      }, 250)
    })

    on('body', 'click', '.js-search-clear', (e) => {
      const form = e.target.closest('.js-search-form')
      this.searchClear(form)
    })

    on('body', 'click', '.js-search-toggle', (e) => {
      root.classList.contains('is-search-active') ? this.searchClose() : this.searchOpen()
    })

    on('body', 'click', '.js-search-close, .js-g-overlay', () => {
      this.searchClose()
    })

    on('body', 'keydown', 'body', (e) => {
      if (e.key == 'Escape') {
        this.searchClose(true)
      }
    })

    //mobile search
    on('body', 'focusin', '.js-mobile-menu .js-search-input', (e) => {
      const target = e.target
      const searchParent = target.closest('.js-g-search')
      searchParent.classList.add('is-mobile-search-active')
    })

    on('body', 'click', '.js-search-close-mobile', (e) => {
      const target = e.target.closest('.js-search-close-mobile')
      const searchParent = target.closest('.js-g-search')
      searchParent.classList.remove('is-mobile-search-active')
    })

    on('body', 'click', '[data-search-url-apply]', (e) => {
      const target = e.target.closest('[data-search-url-apply]')

      setTimeout(() => {
        const parent = e.target.closest('.js-search-parent').querySelector('.search-content__container.is-active')
        const url = target.dataset.searchUrlApply
        const resultCount = parent.querySelector('[data-search-count]').dataset.searchCount

        const urlParams = new URLSearchParams(url.split('?')[1])
        const query = urlParams.get('q') || ''

        document.title = `Search: ${resultCount} results found for "${query}"`
        window.history.replaceState({}, '', url)
      }, 300)
    })
  },
  init: function () {
    this.initSearchPanel()
    this.checkInitialArticleSearch()
  },
}

const gPopup = {
  init: function () {
    this.bindEvents()
  },

  openPopup: function () {
    root.classList.add('is-popup-active')
    globalPopup.setAttribute('aria-hidden', false)
    globalPopup.setAttribute('tabindex', 0)
    globalOverlay.setAttribute('aria-hidden', false)
    globalOverlay.setAttribute('tabindex', 0)
    scrollHandler.scrollDisable()
  },

  closePopup: function () {
    root.classList.remove('is-popup-active')
    globalPopup.setAttribute('aria-hidden', true)
    globalPopup.setAttribute('tabindex', -1)

    globalOverlay.setAttribute('aria-hidden', true)
    globalOverlay.setAttribute('tabindex', -1)
    scrollHandler.scrollEnable()
  },

  bindEvents: function () {
    //match navigation with content
    on('body', 'click', '[data-popup-trigger]', (e) => {
      this.openPopup()
    })

    on('body', 'click', '[data-popup-close], .js-g-overlay', (e) => {
      this.closePopup()
    })

    on('body', 'keydown', 'body', (e) => {
      if (e.key == 'Escape') {
        this.closePopup()
      }
    })
  },
}

on('body', 'click', '.js-toggle-read-more', (e) => {
  const target = e.target
  const parent = target.closest('[data-read-more]')

  parent.dataset.readMore = parent.dataset.readMore == 'false' ? true : false
})

const gScrollSpy = {
  // DOM elements cache
  elements: {
    scrollSpyParents: null,
    observerInstances: [],
  },

  // Create intersection observer for a parent container
  createObserver: function (parent) {
    const triggers = parent.querySelectorAll('[data-scroll-spy-trigger]')
    const targets = parent.querySelectorAll('[data-scroll-spy-target]')

    if (!triggers.length || !targets.length) return

    // Observer options - trigger when section is in the middle of viewport
    const options = {
      root: null,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0,
    }

    // Create observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const targetId = entry.target.getAttribute('data-scroll-spy-target')
          this.updateActiveState(triggers, targetId)
        }
      })
    }, options)

    // Observe all targets
    targets.forEach((target) => {
      observer.observe(target)
    })

    // Store observer instance for cleanup if needed
    this.elements.observerInstances.push(observer)

    // Add click handlers for smooth scrolling
    this.addClickHandlers(triggers, parent)
  },

  // Update active state on navigation items
  updateActiveState: function (triggers, activeId) {
    triggers.forEach((trigger) => {
      const triggerId = trigger.getAttribute('data-scroll-spy-trigger')
      if (triggerId === activeId) {
        trigger.classList.add('is-active')
      } else {
        trigger.classList.remove('is-active')
      }
    })
  },

  // Add smooth scroll click handlers
  addClickHandlers: function (triggers, parent) {
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        const triggerId = trigger.getAttribute('data-scroll-spy-trigger')
        if (!triggerId) return

        const target = parent.querySelector(`[data-scroll-spy-target="${triggerId}"]`)

        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      })
    })
  },

  setupNavigationToggle: function () {
    // product sections navigation
    on('body', 'click', '.js-navigation-toggle', (e) => {
      const target = e.target.closest('.js-navigation-toggle')
      const parent = target.closest('.js-navigation')

      parent.classList.toggle('is-active', !parent.classList.contains('is-active'))
    })
  },

  setupOutsideClickHandler: function () {
    document.addEventListener('click', (e) => {
      const navigationElements = document.querySelectorAll('.js-navigation')
      let clickedOutside = true

      // Check if click was inside ANY of the navigation elements
      navigationElements.forEach((element) => {
        if (element.contains(e.target)) {
          clickedOutside = false
        }
      })

      // If clicked outside all navigation elements, remove the active class
      if (clickedOutside && navigationElements.length > 0) {
        navigationElements.forEach((nav) => {
          nav.classList.remove('is-active')
        })
      }
    })
  },

  // Process all scroll spy parents
  processAllElements: function () {
    if (!this.elements.scrollSpyParents || this.elements.scrollSpyParents.length === 0) return

    this.elements.scrollSpyParents.forEach((parent) => {
      this.createObserver(parent)
    })
  },

  // Cleanup observers (optional, for SPA navigation)
  destroy: function () {
    this.elements.observerInstances.forEach((observer) => {
      observer.disconnect()
    })
    this.elements.observerInstances = []
  },

  // Initialize the scroll spy functionality
  init: function () {
    const elements = document.querySelectorAll('.js-scroll-spy-parent')

    if (elements && elements?.length > 0) {
      this.elements.scrollSpyParents = elements
      this.processAllElements()
      this.setupNavigationToggle()
      this.setupOutsideClickHandler()
    }
  },
}

class LocalizationForm extends HTMLElement {
  constructor() {
    super()
    const disableAutoSubmit = !this.hasAttribute('data-disable-auto-submit')
    const selects = this.querySelectorAll('select')
    if (disableAutoSubmit) {
      selects.forEach((select) => {
        // Auto-size on initial load
        // this.resizeSelectElementToCurrentValue(select);
        // Auto-size when selection changes
        select.addEventListener('change', (event) => {
          this.onSelectChange(event)
          // this.resizeSelectElementToCurrentValue(event.target);
        })
      })
      // // Handle breakpoint change (1024px)
      // const mediaQuery = window.matchMedia("(min-width: 1024px)");
      // mediaQuery.addEventListener("change", () => {
      // 	selects.forEach((select) => {
      // 		this.resizeSelectElementToCurrentValue(select);
      // 	});
      // });
    }
  }

  resizeSelectElementToCurrentValue(select) {
    // Skip if element is not visible
    if (!select.offsetParent) {
      return
    }

    const purgatory = []
    for (const option of select.querySelectorAll('option')) {
      purgatory.push(option)
      if (option.getAttribute('value') != select.value) {
        option.remove()
      }
    }

    select.style.width = ''
    setTimeout(() => {
      const width = select.clientWidth
      if (width > 0) {
        select.style.width = `${width + 8}px`
      }
    }, 100)

    select.append(...purgatory)
  }

  onSelectChange(event) {
    const form = event.target.closest('form')

    // Prevent auto-submit ONLY for forms with the attribute
    if (form && !this.hasAttribute('data-disable-auto-submit')) {
      form.submit()
    }
  }
}

class GLocalizationPopup {
  constructor() {
    this.popupTrigger = document.querySelector('.js-localization-toggle')
    this.popup = document.querySelector('.js-g-localization-popup')
    this.form = this.popup?.querySelector('form')
    this.countrySelect = this.popup?.querySelector('select[name="country_code"]')
    this.languageSelectWrapper = this.popup?.querySelector('.g-localization__language-wrapper')
    this.languageSelect = this.popup?.querySelector('select[name="language_code"]')

    this.localStorageKey = 'gLocalizationCompleted'
    this.localStorageSetupKey = 'gLocalizationSetupTime'
    this.popupExpiryDays = 7

    if (!this.popup) return

    // Load country-language JSON dataset
    this.countriesData = this.getCountryLanguageData()

    this.init()
  }

  init() {
    const now = Date.now()
    const setupTime = localStorage.getItem(this.localStorageSetupKey)

    if (!setupTime) {
      // first time setup
      localStorage.setItem(this.localStorageSetupKey, now)
    } else if (now - setupTime > this.popupExpiryDays * 24 * 60 * 60 * 1000) {
      // expired: reset completion
      localStorage.removeItem(this.localStorageKey)
      localStorage.setItem(this.localStorageSetupKey, now)
    }

    if (localStorage.getItem(this.localStorageKey) !== 'true') {
      // Detect location
      this.detectLocationOnLoad()
    }

    // US shipping country: only English and Spanish in the language selector
    if (this.countrySelect?.value === 'US') {
      this.updateLanguageSelector('US')
    }

    if (this.languageSelect && this.languageSelectWrapper && this.languageSelect.querySelectorAll('option').length === 1) {
      this.languageSelectWrapper.style.display = 'none';
    }

    // Bind events
    this.bindEvents()
  }

  bindEvents() {
    // Close popup
    on('body', 'click', '.js-g-localization-popup-close', () => {
      this.closePopup()
    })

    on('body', 'keydown', 'body', (e) => {
      if (e.key == 'Escape') {
        this.closePopup()
      }
    })

    // Country change → update language selector
    on('body', 'change', '#CountrySelect-popup', (e) => {
      const select = e.target
      const selected = select.value
      const selectedOption = select.options[select.selectedIndex]
      const countryUrl = selectedOption.dataset.countryUrl
      const form = selectedOption.closest('form')

      if (countryUrl) {
        form.setAttribute('data-country-url', countryUrl)
        this.languageSelect.value = 'en'
        this.languageSelect.disabled = true
        this.languageSelectWrapper.style.display = 'none'
      } else {
        this.updateLanguageSelector(selected)
        form.removeAttribute('data-country-url')
        this.languageSelect.disabled = false
      }
    })

    on('body', 'click', '.g-localization__submit', (e) => {
      e.preventDefault()
      const form = e.target.closest('form')
      if (form.dataset.countryUrl) {
        window.location = form.dataset.countryUrl
      } else {
        this.submitForm()
      }
    })

    on('body', 'click', '.js-localization-toggle', (e) => {
      const trigger = e.target.closest('.js-localization-toggle')
      if (!trigger) return
      const focusReturn =
        trigger.closest('.oddit-header__mobile-menu')
          ? trigger.closest('.oddit-header')?.querySelector('.oddit-header__menu-toggle')
          : trigger
      root.classList.contains('is-localization-popup-active') ? this.closePopup() : this.openPopup(focusReturn)
    })
  }

  submitForm() {
    localStorage.setItem(this.localStorageKey, 'true')
    this.form.submit()
    this.closePopup()
  }

  openPopup(trigger = null) {
    const focusReturn = trigger || this.popupTrigger || null
    root.classList.add('is-localization-popup-active')
    setAriaHiddenAttributes(this.popup, true)
    scrollHandler.scrollDisable()
    focusTrapManager.activate(this.popup, focusReturn)
  }

  closePopup() {
    root.classList.remove('is-localization-popup-active')
    localStorage.setItem(this.localStorageKey, 'true')
    setAriaHiddenAttributes(this.popup, false)
    scrollHandler.scrollEnable()

    focusTrapManager.deactivate(this.popup)
  }

  // --------------------------
  // LOCATION DETECTION ON LOAD (Shopify API)
  // --------------------------
  async detectLocationOnLoad() {
    if (!window.Shopify) {
      console.warn('[POPUP] Shopify object not found')
      return
    }

    const url =
      window.Shopify.routes.root +
      'browsing_context_suggestions.json' +
      '?country[enabled]=true' +
      `&country[exclude]=${window.Shopify.country}` +
      '&language[enabled]=true' +
      `&language[exclude]=${window.Shopify.language}`

    try {
      const res = await fetch(url)
      const data = await res.json()

      const detectedCountry = data?.detected_values?.country?.name
      const detectedCode = data?.detected_values?.country?.handle

      if (!detectedCountry || !detectedCode) {
        return
      }

      // Only show popup if detected country differs from current site's country
      if (detectedCode === window.Shopify.country) {
        // User is in the correct region — don't show popup
        localStorage.setItem(this.localStorageKey, 'true')
        return
      }

      // Show popup when detectedCode !== window.Shopify.country
      setTimeout(() => {
        root.classList.add('is-localization-popup-active')
        setAriaHiddenAttributes(this.popup, true)
      }, 500)

      // Check if detected country exists in our countriesData
      const countryData = this.countriesData.find((c) => c.iso_code === detectedCode)

      if (!countryData) {
        return
      }

      // Update country select
      if (this.countrySelect) {
        const matchOption = [...this.countrySelect.options].find((opt) => opt.value === detectedCode)

        if (matchOption) {
          this.countrySelect.value = detectedCode
        }
      }

      // Update language selector
      if (this.languageSelect) {
        this.updateLanguageSelector(detectedCode)
      }
    } catch (err) {
      console.warn('[POPUP] Shopify location detection failed:', err)
    }
  }
  getCountryLanguageData() {
    const el = document.getElementById('g-localization-country-data')
    if (!el) {
      return []
    }
    try {
      const data = JSON.parse(el.textContent)
      return data.countries
    } catch (err) {
      return []
    }
  }
  updateLanguageSelector(countryCode) {
    if (!this.languageSelect) return
    const country = this.countriesData.find((c) => c.iso_code === countryCode)

    if (!country) {
      return
    }

    // US shipping country only: restrict to English and Spanish
    let languages = country.languages || []
    if (countryCode === 'US') {
      languages = languages.filter((lang) => {
        const base = (lang.iso_code || '').split('-')[0]
        return base === 'en' || base === 'es'
      })
    }

    const previousValue = this.languageSelect.value

    // Clear existing options
    this.languageSelect.innerHTML = ''

    // Add new options
    languages.forEach((lang) => {
      const opt = document.createElement('option')
      opt.value = lang.iso_code
      opt.textContent = lang.endonym_name
      opt.setAttribute('lang', lang.iso_code)
      this.languageSelect.appendChild(opt)
    })

    if (languages.some((lang) => lang.iso_code === previousValue)) {
      this.languageSelect.value = previousValue
    } else {
      // Current language was filtered out (e.g. FR on US) — fall back so UI matches submit
      const fallback =
        languages.find((lang) => (lang.iso_code || '').split('-')[0] === 'en') ||
        languages.find((lang) => (lang.iso_code || '').split('-')[0] === 'es') ||
        languages[0]
      if (fallback) {
        this.languageSelect.value = fallback.iso_code
      }
    }

    if (this.languageSelectWrapper) {
      this.languageSelectWrapper.style.display = languages.length > 1 ? 'block' : 'none'
    }
  }
}

customElements.define('localization-form', LocalizationForm)

const playTargetVideo = (video, externalVideoType, player, soundOn = false, videoHasMuteBtn = false) => {
  const playVideo = (video) => {
    video.muted = !(videoHasMuteBtn && soundOn)
    video.play()
  }

  const playExternalVideo = (player, externalVideoType) => {
    if (externalVideoType === 'vimeo') {
      player.setMuted(!(videoHasMuteBtn && soundOn))
      player.play().catch((error) => {
        if (error.name !== 'PlayInterrupted') {
          console.error('Error playing video:', error)
        }
      })
    } else if (externalVideoType === 'youtube') {
      // Both functions existing means the YouTube player is fully initialized.
      if (typeof player.playVideo == 'function' && typeof player.mute === 'function') {
        videoHasMuteBtn && soundOn ? player.unMute() : player.mute()
        player.playVideo()
      } else {
        console.warn('YouTube player not fully initialized or methods unavailable.')
      }
    }
  }

  if (video) {
    playVideo(video)
  } else if (player) {
    playExternalVideo(player, externalVideoType)
  }
}

const pauseTargetVideo = (video, externalVideoType, player) => {
  const pauseVideo = (video) => {
    video.pause()
    video.muted = true
  }

  const pauseExternalVideo = (player, externalVideoType) => {
    if (externalVideoType === 'vimeo') {
      player.pause().catch((error) => {
        if (error.name !== 'PlayInterrupted') {
          console.error('Error pausing video:', error)
        }
      })
      player.setMuted(true)
    } else if (externalVideoType === 'youtube') {
      // Both functions existing means the YouTube player is fully initialized.
      if (typeof player.pauseVideo === 'function' && typeof player.mute === 'function') {
        player.pauseVideo()
        player.mute()
      } else {
        console.warn('YouTube player not fully initialized or methods unavailable.')
      }
    }
  }

  if (video) {
    pauseVideo(video)
  } else if (player) {
    pauseExternalVideo(player, externalVideoType)
  }
}

const loadLazyVideo = (videoEl) => {
  if (videoEl.dataset.loaded) return

  const sources = videoEl.querySelectorAll('source')
  sources.forEach((source) => {
    if (!source.src && source.dataset.src) {
      source.src = source.dataset.src
    }
  })

  if (videoEl.readyState === 0) {
    videoEl.load()
    videoEl.dataset.loaded = 'true'
  }
}

const initVideo = () => {
  const handleVideoPlayback = (item, play, targetPlayer) => {
    const videoElement = item.querySelector('video')
    const externalVideo = item.querySelector('.js-external-video')
    const externalVideoType = externalVideo?.dataset.videoHost
    const videoHasMuteBtn = item.querySelector('.js-video-mute.is-active, .js-video-unmute.is-active')
    const soundOn = videoHasMuteBtn?.classList.contains('js-video-mute')
    if (!videoElement && !targetPlayer) {
      return
    }

    if (play) {
      playTargetVideo(videoElement, externalVideoType, targetPlayer, soundOn, videoHasMuteBtn)
    } else {
      pauseTargetVideo(videoElement, externalVideoType, targetPlayer)
    }
  }

  const handleVideoVisibility = (entry, item) => {
    const isVisible = entry.isIntersecting
    const videoButtons = item.querySelectorAll('.js-video-mute, .js-video-unmute, .js-video-play, .js-video-pause')
    const externalVideo = item.querySelector('.js-external-video')
    const externalVideoId = externalVideo?.dataset.videoId
    const targetPlayer = allPlayersObj[externalVideoId]
    handleVideoPlayback(item, isVisible, targetPlayer)
    // Reset video buttons and toggle based on visibility
    videoButtons.forEach((button) => button.classList.remove('is-active'))
    if (isVisible) {
      item.querySelector('.js-video-unmute')?.classList.add('is-active')
      item.querySelector('.js-video-pause')?.classList.add('is-active')
    }
  }

  // always play/pause video if section is in view
  document.querySelectorAll('.c-video').forEach((item) => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        handleVideoVisibility(entry, item)

        if (entry.isIntersecting) {
          const lazyVideo = item.querySelector('video.js-lazy-video')
          lazyVideo && loadLazyVideo(lazyVideo)
        }
      },
      { root: null, threshold: [0.4] }
    )
    observer.observe(item)
  })

  const muteOtherVideos = (allPlayersObj, currentPlayer, videoType) => {
    let allPlayers = Object.values(allPlayersObj)

    allPlayers.forEach((player) => {
      if (player === currentPlayer) {
        return
      }
      const videoContainer = player.closest('.c-video')
      videoContainer.querySelector('.js-video-mute')?.classList.remove('is-active')
      videoContainer.querySelector('.js-video-unmute')?.classList.add('is-active')

      if (videoType === 'youtube' && typeof player.mute === 'function') {
        player.mute()
      } else if (videoType === 'vimeo' && typeof player.setMuted === 'function') {
        player.setMuted(true)
      } else if (player.tagName === 'VIDEO') {
        player.muted = true
      }
    })
  }

  const handleVideoControl = (e, action) => {
    const target = e.target.closest(`.js-video-${action}`)
    const videoContainer = target.closest('.c-video')
    const videoElement = videoContainer.querySelector('video')
    const externalVideo = videoContainer.querySelector('.js-external-video')
    const videoHasMuteBtn = videoContainer.querySelector('.js-video-mute.is-active, .js-video-unmute.is-active')
    const soundOn = videoHasMuteBtn?.classList.contains('js-video-mute')
    const externalVideoType = externalVideo?.dataset.videoHost
    const externalVideoId = externalVideo?.dataset.videoId
    const targetPlayer = allPlayersObj[externalVideoId]

    if (action === 'play') {
      playTargetVideo(videoElement, externalVideoType, targetPlayer, soundOn, videoHasMuteBtn)
    } else if (action === 'pause') {
      pauseTargetVideo(videoElement, externalVideoType, targetPlayer)
    } else if (action === 'mute' || action === 'unmute') {
      const isUnmuting = action === 'unmute' // Flag to check for the action

      if (externalVideoType === 'youtube') {
        if (typeof targetPlayer.unMute == 'function') {
          const willBeUnmuted = isUnmuting || targetPlayer.isMuted()
          targetPlayer.isMuted() ? targetPlayer.unMute() : targetPlayer.mute()

          if (willBeUnmuted) {
            muteOtherVideos(allPlayersObj, targetPlayer, 'youtube')
          }
        } else {
          console.warn('YouTube player not fully initialized or methods unavailable.')
        }
      } else if (externalVideoType === 'vimeo') {
        targetPlayer.getMuted().then((muted) => {
          const willBeUnmuted = isUnmuting || muted
          targetPlayer.setMuted(!muted)
          if (willBeUnmuted) {
            muteOtherVideos(allPlayersObj, targetPlayer, 'vimeo')
          }
        })
      } else if (videoElement) {
        const willBeUnmuted = isUnmuting || videoElement.muted
        videoElement.muted = action === 'mute' ? true : false
        if (willBeUnmuted) {
          muteOtherVideos(allPlayersObj, videoElement, 'html5')
        }
      }
    }

    // Toggle play/pause or mute/unmute button visibility
    if (action === 'play' || action === 'pause') {
      target.classList.remove('is-active')
      videoContainer.querySelector(`.js-video-${action === 'play' ? 'pause' : 'play'}`)?.classList.add('is-active')
    } else if (action === 'mute' || action === 'unmute') {
      videoContainer.querySelector('.js-video-mute')?.classList.toggle('is-active', action === 'unmute')
      videoContainer.querySelector('.js-video-unmute')?.classList.toggle('is-active', action === 'mute')
    }
  }

  on('body', 'click', '.js-video-mute', (e) => handleVideoControl(e, 'mute'))
  on('body', 'click', '.js-video-unmute', (e) => handleVideoControl(e, 'unmute'))
  on('body', 'click', '.js-video-play', (e) => handleVideoControl(e, 'play'))
  on('body', 'click', '.js-video-pause', (e) => handleVideoControl(e, 'pause'))

  on('body', 'click', '.js-product-hero-slider-thumbnail', (e) => {
    // Note: The setFirstActiveImage function triggers this click event
    const sliderSelector = `.js-product-hero-slider${window.innerWidth <= 1024 ? '' : '.is-scroll'}`
    const slider = e.target.closest(sliderSelector)

    // Exit if no slider or no video in the slider
    if (!slider || !slider.querySelector('.c-video')) return

    // Pause all videos in the slider
    slider.querySelectorAll('.c-video').forEach((videoContainer) => {
      const videoElement = videoContainer.querySelector('video')
      const externalVideo = videoContainer.querySelector('.js-external-video')
      const externalVideoType = externalVideo?.dataset.videoHost
      const externalVideoId = externalVideo?.dataset.videoId
      const targetPlayer = externalVideoId ? allPlayersObj[externalVideoId] : null

      pauseTargetVideo(videoElement, externalVideoType, targetPlayer)

      // Reset video buttons
      videoContainer
        .querySelectorAll('.js-video-mute, .js-video-unmute, .js-video-play, .js-video-pause')
        .forEach((button) => button.classList.remove('is-active'))
    })

    // Play the target video after a short delay
    setTimeout(() => {
      const activeVideoContainer = slider.querySelector('.js-slider-block.is-active .c-video')
      if (!activeVideoContainer) return

      const videoElement = activeVideoContainer.querySelector('video')
      const externalVideo = activeVideoContainer.querySelector('.js-external-video')
      const externalVideoType = externalVideo?.dataset.videoHost
      const externalVideoId = externalVideo?.dataset.videoId
      const targetPlayer = externalVideoId ? allPlayersObj[externalVideoId] : null

      playTargetVideo(videoElement, externalVideoType, targetPlayer)

      activeVideoContainer
        .querySelectorAll('.js-video-unmute, .js-video-pause')
        .forEach((button) => button.classList.add('is-active'))
    }, 100)
  })
}

const pdp = {
  productHero: document.querySelector('.product-hero'),

  setupVideoReframe: function () {
    // reframe for .is-fade.is-natural
    reframe('.is-fade.is-natural .c-video iframe', 'c-video__reframe')
  },

  setupModelViewers: function () {
    // set modelViewer attributes
    if (!this.productHero) return

    this.productHero.querySelectorAll('model-viewer').forEach((model) => {
      const attributes = {
        'disable-zoom': '',
        // Add more attributes here if needed
      }
      Object.keys(attributes).forEach((key) => {
        model.setAttribute(key, attributes[key])
      })
    })
  },

  setupBenefitsAccordion: function () {
    const $benefitsSection = document.querySelector('.product-sections-benefits')
    if (!$benefitsSection) return

    const $firstAccordionToggle = $benefitsSection.querySelector(
      'c-accordion:first-of-type .js-accordion-toggle'
    )
    if ($firstAccordionToggle) $firstAccordionToggle.click()
  },

  setupStickyPurchaseBar: function () {
    if (!this.productHero) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // User scrolled back to hero - remove class
            root.classList.remove('is-sticky-purchase-bar-active')
          } else {
            // User scrolled past hero - add class
            root.classList.add('is-sticky-purchase-bar-active')
          }
        })
      },
      {
        threshold: 0.4,
        rootMargin: '0px',
      }
    )

    observer.observe(this.productHero)

    // Detect when footer appears and remove sticky class
    const footerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            root.classList.remove('is-sticky-purchase-bar-active')
          } else {
            // Footer is not visible - check if hero is also not visible, then add class back
            const footerAndHeroNotVisible = this.productHero.getBoundingClientRect().bottom > 0
            if (!footerAndHeroNotVisible) {
              root.classList.add('is-sticky-purchase-bar-active')
            }
          }
        })
      },
      {
        threshold: 0.1,
        rootMargin: '0px',
      }
    )

    const footer = document.querySelector('.g-footer')
    if (footer) {
      footerObserver.observe(footer)
    }

    on('body', 'click', '.js-purchase-bar-toggle', (e) => {
      let trigger = e.target.closest('.js-purchase-bar-toggle')
      const purchaseBar = trigger.closest('.js-purchase-bar')

      purchaseBar.classList.toggle('is-selling-plan-active', !purchaseBar.classList.contains('is-selling-plan-active'))
    })
  },

  init: function () {
    this.setupVideoReframe()
    this.setupModelViewers()
    this.setupStickyPurchaseBar()
    this.setupBenefitsAccordion()
  },
}

const initMatch = () => {
  function openTarget(trigger) {
    //trigger remove and add active state
    const parent = trigger.closest('[data-match-parent]')
    const triggers = parent.querySelectorAll('[data-match-trigger]')
    const activeTarget = parent.querySelector(`[data-match-target="${trigger.dataset.matchTrigger}"]`)

    triggers.forEach((item) => {
      item.classList.remove('is-active')
    })
    trigger.classList.add('is-active')

    parent.querySelectorAll('[data-match-target]').forEach((item) => {
      item.classList.remove('is-active')
    })
    activeTarget.classList.add('is-active')
  }

  //match navigation with content
  on('body', 'click', '[data-match-trigger]', (e) => {
    let trigger = e.target.closest('[data-match-trigger]')
    openTarget(trigger)
  })
}

// Load Vimeo / Youtube players
let allPlayersObj = {}

const initAllVideoPlayers = () => {
  const loadPromises = []

  document.querySelectorAll('.c-video video:not(.js-external-video)').forEach((video) => {
    const videoId = video.id || `native-${Math.random().toString(36).substring(2, 9)}`
    video.id = videoId
    allPlayersObj[videoId] = video
  })

  document.querySelectorAll('.js-external-video[data-video-host="vimeo"]').forEach((video) => {
    const externalVideoId = video.dataset.videoId
    const player = new Vimeo.Player(video)

    const vimeoPromise = new Promise((resolve) => {
      player.on('loaded', () => {
        allPlayersObj[externalVideoId] = player
        resolve()
      })
    })
    loadPromises.push(vimeoPromise)
  })

  const youtubePromise = new Promise((resolve) => {
    // youtube fix - lace this event at global level
    window.onYouTubeIframeAPIReady = () => {
      document.querySelectorAll('.js-external-video[data-video-host="youtube"]').forEach((video) => {
        const externalVideoId = video.dataset.videoId
        const player = new YT.Player(video, {
          videoId: externalVideoId,
          playerVars: { playsinline: 1 },
        })
        allPlayersObj[externalVideoId] = player
      })
    }

    window.onYouTubeIframeAPIReady = () => {
      document.querySelectorAll('.js-external-video[data-video-host="youtube"]').forEach((video) => {
        const externalVideoId = video.dataset.videoId
        new YT.Player(video, {
          videoId: externalVideoId,
          playerVars: { playsinline: 1 },
          events: {
            onReady: (event) => {
              allPlayersObj[externalVideoId] = event.target
            },
          },
        })
      })

      // YouTube doesn't have a single "all players ready" event,
      // so we resolve the main promise after a short, final delay
      // to ensure all individual players have fired their 'onReady' events.
      // This is the common pattern for YT API initialization.
      setTimeout(resolve, 300)
    }

    // If the YT API script hasn't loaded yet, do nothing.
    // If it's already loaded, onYouTubeIframeAPIReady will be called immediately
    // after it's defined.
  })

  // Only add the promise if there are YouTube videos to prevent indefinite waiting
  if (document.querySelector('.js-external-video[data-video-host="youtube"]')) {
    loadPromises.push(youtubePromise)
  }

  return Promise.all(loadPromises)
}

window.addEventListener('load', () => {
  initAllVideoPlayers()
    .then(() => {
      initVideo()
    })
    .catch((error) => {
      initVideo()
    })
})

// Safely run a function and catch errors
function safeRun(fn) {
  try {
    fn?.()
  } catch (error) {
    console.error(`Init error in ${fn.name || 'anonymous function'}:`, error)
  }
}

document.addEventListener('DOMContentLoaded', () => {
  gHeader.init()
  gPopup.init()
  gScrollSpy.init()
  new GLocalizationPopup()
  // Run common initializations
  ;[
    initVideo,
    initMatch,

    // ...
  ].forEach(safeRun)

  new CursorFollowLabel()
  // Execute page-specific functions
  const isTemplatePage = (templateName) => root.classList.contains(templateName)

  if (isTemplatePage('template-product')) {
    pdp.init()
  }
  // Add more page-specific initializations as needed
})
